import confetti from 'canvas-confetti';
import { useCallback } from 'react';

interface ConfettiOptions {
  particleCount?: number;
  spread?: number;
  origin?: { x: number; y: number };
  colors?: string[];
  scalar?: number;
}

export function useConfetti() {
  const fireConfetti = useCallback((options?: ConfettiOptions) => {
    const defaults: ConfettiOptions = {
      particleCount: 100,
      spread: 70,
      origin: { x: 0.5, y: 0.5 },
      colors: ['#1ABC9C', '#16A085', '#2ECC71', '#27AE60', '#3498DB'],
      scalar: 1,
    };

    const mergedOptions = { ...defaults, ...options };

    confetti({
      particleCount: mergedOptions.particleCount,
      spread: mergedOptions.spread,
      origin: mergedOptions.origin,
      colors: mergedOptions.colors,
      scalar: mergedOptions.scalar,
      disableForReducedMotion: true,
    });
  }, []);

  const fireTaskComplete = useCallback(() => {
    // Subtle confetti burst for task completion
    confetti({
      particleCount: 50,
      spread: 60,
      origin: { x: 0.5, y: 0.6 },
      colors: ['#1ABC9C', '#2ECC71', '#3498DB'],
      scalar: 0.8,
      gravity: 1.2,
      decay: 0.94,
      disableForReducedMotion: true,
    });
  }, []);

  const fireCelebration = useCallback(() => {
    // Big celebration confetti
    const duration = 2000;
    const animationEnd = Date.now() + duration;
    const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 9999 };

    const randomInRange = (min: number, max: number) => Math.random() * (max - min) + min;

    const interval = setInterval(() => {
      const timeLeft = animationEnd - Date.now();

      if (timeLeft <= 0) {
        return clearInterval(interval);
      }

      const particleCount = 50 * (timeLeft / duration);

      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 },
        colors: ['#1ABC9C', '#16A085', '#2ECC71', '#27AE60'],
      });
      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 },
        colors: ['#3498DB', '#2980B9', '#9B59B6', '#8E44AD'],
      });
    }, 250);

    return () => clearInterval(interval);
  }, []);

  return { fireConfetti, fireTaskComplete, fireCelebration };
}
